<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2 id="prenatal-rapid-genomic-trio-test-title">{{ testName }}</h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="prenatal-rapid-exome-trio-tes-grid"
      >
        <v-container>
          <v-row>
            <v-col>
              If both parental samples are EDTA blood, this test will proceed as
              a prenatal rapid <b>whole genome sequencing (WGS)</b> trio for the
              same price. If saliva is received for one or both parents, testing
              will proceed as a prenatal rapid <b>exome</b> sequencing trio.
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              Turnaround time: 15 working days
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Funding source*</span>
              <v-select
                item-disabled=""
                v-model="billTo"
                :items="billingOptions"
                item-text="key"
                item-value="value"
                :id="testId + '.billTo'"
                outlined
                :rules="ruleNotNull"
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <v-alert
                text
                outlined
                type="warning"
                icon="mdi-cash-usd-outline"
                class="mr-1 pb-0 pl-0"
              >
                <p>Patient to be referred to a genetics clinic for testing.</p>
                <p>
                  Only Likely Pathogenic and Pathogenic (class 4 and 5) variants
                  in genes relevant to the clinical indication are reported.
                </p>
              </v-alert>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <b class="text-title">Clinical features</b>
              <template v-if="brokenPhenotypesApi">
                <BrokenApiAlert type="phenotypes" />
              </template>
              <template v-else>
                <v-autocomplete
                  v-model="phenotypes"
                  :id="testId + '.phenotypes'"
                  :items="phenotypeList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  deletable-chips
                  @input="searchInputPhenotypes = null"
                  :search-input.sync="searchInputPhenotypes"
                  outlined
                  hide-details="auto"
                  @update:search-input="handlePhSearchChange"
                  :filter="filterPhItems"
                >
                  <template v-slot:prepend-item v-if="isSearchingHp">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ phSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint"
                  >Please start typing the main clinical features. e.g.
                  intellectual disability. Select the most appropriate HPO term
                  from those suggested. Detailed clinical information helps us
                  diagnose patients more accurately. The phenotype's are sourced
                  from hpo.jax.org.</span
                >
              </template>
              <template v-if="badSearchQuery">
                <BadSearchQueryAlert />
              </template>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical notes</span>
              <v-textarea
                rows="2"
                auto-grow
                v-model="clinicalNotes"
                :id="testId + '.clinicalNotes'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. results of relevant prior genetic and non-genetic
                investigations, increased chance on carrier screening,
                ultrasound findings, biochemistry etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family history</span>
              <v-textarea
                rows="1"
                auto-grow
                v-model="familyHx"
                :id="testId + '.familyHx'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. affected status of the parents, parental consanguinity,
                recurrent miscarriage, etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <v-checkbox
                v-model="suspectDiagnosis"
                :id="testId + '.suspectDiagnosis'"
                item-text="key"
                item-value="value"
                label="I strongly suspect a specific clinical diagnosis? E.g. Kabuki syndrome"
                hide-details="auto"
              >
              </v-checkbox>
            </v-col>
          </v-row>

          <v-row v-if="suspectDiagnosis">
            <v-col class="offset-1">
              <span class="text-title"
                >Please provide your top gene candidates</span
              >
              <template v-if="brokenGenesApi">
                <BrokenApiAlert type="genes" />
              </template>
              <template v-else>
                <v-autocomplete
                  v-model="indGenes"
                  :id="testId + '.indGenes'"
                  :items="validGenesList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  persistent-hint
                  deletable-chips
                  @input="searchInputGenes = null"
                  :search-input.sync="searchInputGenes"
                  outlined
                  hide-details="auto"
                  @update:search-input="handleGenesSearchChange"
                  :filter="filterGenesItems"
                  counter="5"
                  :rules="ruleMaxCustomGenes"
                >
                  <template v-slot:prepend-item v-if="isSearchingGenes">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ genesSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint ">Select up to 5 genes.</span>
              </template>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <b class="text-title">Gene panels</b>
              <template v-if="brokenGenePanelsApi">
                <BrokenApiAlert type="genePanels" />
              </template>
              <template v-else>
                <v-autocomplete
                  v-model="genePanels"
                  :id="testId + '.genePanels'"
                  :items="genePanelsList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  item-color="blue"
                  deletable-chips
                  outlined
                  hide-details="auto"
                  @input="searchInputPanels = null"
                  :search-input.sync="searchInputPanels"
                  @update:search-input="handleGenePanelsSearchChange"
                  :filter="filterGenePanelsItems"
                >
                  <template v-slot:prepend-item v-if="isSearchingGenePanels">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ genePanelsSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint"
                  >Please choose the most relevant gene panels to be used in
                  analysis. You can search panels using a key word. e.g.
                  congenital. The list will refine based on the word
                  entered.</span
                >
              </template>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <v-alert text color="info" outlined>
                <p>
                  Detailed information on panel content is available at
                  <a href="https://panelapp-aus.org/" target="_blank"
                    ><b style="color: #00538b">PanelApp Australia</b></a
                  >
                </p>
              </v-alert>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <FamilyMember
                style="grid-area: person1"
                title="Family member 1"
                :idPrefix="testId + '.fm0.'"
                ref="familyMember1"
                :patientdata="patientdata"
                familytype="Trio"
                :populatekeys="[
                  'surname',
                  'country',
                  'unitFlat',
                  'streetAddress',
                  'suburb',
                  'postcode',
                  'state',
                ]"
                testType="prenatal_parents"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <FamilyMember
                style="grid-area: person2"
                title="Family member 2"
                :idPrefix="testId + '.fm1.'"
                ref="familyMember2"
                :patientdata="patientdata"
                :familytype="'Trio'"
                :populatekeys="[
                  'surname',
                  'country',
                  'unitFlat',
                  'streetAddress',
                  'suburb',
                  'postcode',
                  'state',
                ]"
                testType="prenatal_parents"
              />
            </v-col>
          </v-row>
        </v-container>

        <PrenatalConsentContainer
          ref="PrenatalConsentContainer"
          testName="Prenatal rapid genomic trio"
          :testId="testId"
          :is-trio="true"
          :force-form-dirty="forceFormDirty"
        ></PrenatalConsentContainer>

        <TestPriceContainer
          :price="price"
          :showReanalysisAlert="showReanalysisAlert"
        />
      </v-form>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        id="prenatal_rapid_trio_continue_section"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FamilyMember from "../../FamilyMember";
import GenePanelMixin from "../../../mixins/GenePanelMixin";
import PhenotypesMixin from "../../../mixins/PhenotypesMixin";
import FormRulesMixin from "../../../mixins/FormRulesMixin";
import GenesMixin from "../../../mixins/GenesMixin";
import { EventBus } from "@/event-bus";
import {
  useCompletedDetails,
  useSectionStore,
} from "../../../store/SectionStore";
import PrenatalConsentContainer from "./PrenatalConsentContainer.vue";
import TestPriceContainer from "../../common/TestPriceContainer.vue";
import RetrieveJSONMixin from "../../../mixins/RetrieveJSONMixin.vue";
import BrokenApiAlert from "../../common/BrokenApiAlert.vue";
import BadSearchQueryAlert from "../../common/BadSearchQueryAlert.vue";

export default {
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
    showReanalysisAlert: Boolean,
  },
  watch: {
    price: function() {
      this.$emit("toggleTotalPrice", this.price, this.testId);
    },
  },
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.prenatalRapidTrioTestPricing = await this.execRetrieveJson(
      "json/pricing/prenatal_rapid_trio_pricing.json",
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    EventBus.$emit("getPatientData");

    const element = document.getElementById("prenatal-rapid-trio-test-title");

    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }

    const pricing = this.prenatalRapidTrioTestPricing[this.trioFlag];

    this.price = pricing.price;
    this.testCode = pricing.testCode;
    this.medicare = pricing.medicare;

    this.$emit("toggleTotalPrice", this.price, this.testId);

    // update selectedTrioTests in App component
    EventBus.$emit("selectTrioTest", this.testName);
  },
  beforeDestroy() {
    EventBus.$emit("deselectTrioTest", this.testName);
  },
  mixins: [
    GenePanelMixin,
    FormRulesMixin,
    PhenotypesMixin,
    GenesMixin,
    RetrieveJSONMixin,
  ],
  name: "",
  components: {
    FamilyMember,
    PrenatalConsentContainer,
    TestPriceContainer,
    BrokenApiAlert,
    BadSearchQueryAlert,
  },
  data: () => ({
    valid: false,
    urgent: true,
    urgentReason: "",
    prenatalRapidTrioTestPricing: null,
    suspectDiagnosis: "",
    genePanels: [],
    phenotypes: [],
    indGenes: [],
    familyHx: "",
    clinicalNotes: "",
    trioFlag: "Trio",
    billTo: "",
    billingOptions: [
      { key: "Patient / Family", value: "PATIENT" },
      {
        key: "Clinical Service / Institution / Hospital",
        value: "PRACTITIONER",
      },
    ],
    searchInputPanels: null,
    price: null,
    testCode: null,
    medicare: false,
    searchInputPhenotypes: null,
    searchInputGenes: null,
    urgency: "Rapid",
  }),

  methods: {
    isFormValid: function() {
      let isValid;
      if (typeof this.$refs.PrenatalConsentContainer !== "undefined") {
        isValid = this.$refs.PrenatalConsentContainer.isFormValid();
      } else isValid = false;
      return (
        isValid &&
        this.$refs.familyMember1.isFormValid() &&
        this.$refs.familyMember2.isFormValid()
      );
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    transferData: function() {
      let familyDetails = [];
      for (let [familyKey, familyComponent] of Object.entries(this.$refs)) {
        if (familyComponent && familyKey.substring(0, 12) === "familyMember") {
          familyDetails.push(familyComponent.transferData());
        }
      }

      const finalGenePanels = this.getFinalGenePanels(this.genePanels);
      const finalIndGenes = this.getFinalIndGenes(this.indGenes);
      const finalPhenotypes = this.getFinalPhenotypes(this.phenotypes);

      const store = useCompletedDetails();

      // Store test data for Summary screen
      store.prenatal_rapid_genomic_trio = [
        { key: "URGENT", value: this.urgent },
        {
          key: "URGENT REASON",
          value: "Rapid exome testing",
        },
        {
          key: "GENE PANELS",
          value: this.displayPrenatalGenePanels(finalGenePanels),
        },
        {
          key: "INDIVIDUAL GENES",
          value: this.displayPrenatalIndGenesList(finalIndGenes),
        },
        {
          key: "PHENOTYPES",
          value: this.displayPrenatalPhenoList(finalPhenotypes),
        },
        { key: "FAMILY HISTORY", value: this.familyHx },
        { key: "CLINICAL NOTES", value: this.clinicalNotes },
        { key: "BILL TO", value: this.billTo },
        { key: "PRICE", value: this.dollarPrice(this.price) },
      ];
      store.prenatal_rapid_genomic_trio_family = familyDetails;

      let result = {
        urgent: this.urgent,
        urgentReason: "Rapid exome testing",
        testType: this.testId,
        rapid: this.rapidTest,
        genePanels: finalGenePanels,
        indGenes: finalIndGenes,
        phenotypes: finalPhenotypes,
        familyHx: this.familyHx,
        clinicalNotes: this.clinicalNotes,
        family: familyDetails,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        billTo: this.billTo,
        medicare: this.medicare,
        testCode: this.testCode,
      };

      if (this.$refs.PrenatalConsentContainer) {
        const consentData = this.$refs.PrenatalConsentContainer.transferData();

        // assign the data fields from consent direct into the top level of our data
        Object.assign(result, consentData);
      }

      return result;
    },
  },
};
</script>
